import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { LogoIcon } from './Logo';
import LegalGate from './LegalGate';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { query, where, orderBy, limit, updateDoc, doc } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { useCollection, useDocument } from '../hooks/useFirestore';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { reviewsCol, schoolDoc, notificationsCol } from '../lib/firestore';
import app, { db } from '../lib/firebase';
import type { School, UserRole, AppNotification } from '../types';

interface NavItem {
  label: string;
  to: string;
  roles: UserRole[];
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Schools',     to: '/admin',             roles: ['superAdmin'] },
  { label: 'My School',   to: '/school',            roles: ['schoolAdmin'] },
  { label: 'Cycles',      to: '/cycles',            roles: ['schoolAdmin', 'headteacher'] },
  { label: 'To Do',       to: '/todo',              roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'] },
  { label: 'Reviews',     to: '/reviews',           roles: ['headteacher', 'lineManager', 'staff'] },
  { label: 'Documents',   to: '/documents',         roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'] },
  { label: 'CPD Log',     to: '/cpd',               roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'] },
  { label: 'Settings',    to: '/school/settings',   roles: ['schoolAdmin'] },
];

const ROLE_LABELS: Record<UserRole, string> = {
  superAdmin:  'Super Admin',
  schoolAdmin: 'School Admin',
  headteacher: 'Headteacher',
  lineManager: 'Line Manager',
  staff:       'Staff',
};

// ─── Notification count hook ──────────────────────────────────────────────────

function useActionableCount() {
  const { appUser } = useAuth();
  const uid      = appUser?.id ?? '';
  const schoolId = appUser?.schoolId ?? '';
  const role     = appUser?.role;

  const isLM    = role === 'lineManager' || role === 'headteacher';
  const isStaff = role === 'staff';

  // LMs: reviews assigned to them
  const { data: lmReviews } = useCollection(
    uid && schoolId && isLM
      ? query(reviewsCol, where('schoolId', '==', schoolId), where('reviewerId', '==', uid))
      : null,
    uid,
  );

  // Staff: their own reviews
  const { data: staffReviews } = useCollection(
    uid && schoolId && isStaff
      ? query(reviewsCol, where('schoolId', '==', schoolId), where('revieweeId', '==', uid))
      : null,
    uid,
  );

  return useMemo(() => {
    if (isLM) {
      return lmReviews.filter((r) => {
        const rev = r as { status: string; revieweeId?: string; midYearReview?: { staffSubmittedAt?: unknown }; id?: string };
        // Skip reviews where this LM is also the reviewee (handled as staff)
        if (rev.revieweeId === uid) return false;
        if (rev.status === 'submittedByStaff') return true;
        // Mid-year only actionable for LM after staff has submitted their comments
        if (rev.status === 'midYearOpen') return !!(rev.midYearReview?.staffSubmittedAt);
        return false;
      }).length;
    }
    if (isStaff) {
      return staffReviews.filter((r) => {
        const rev = r as { status: string; midYearReview?: { staffSubmittedAt?: unknown } };
        if (rev.status === 'notStarted' || rev.status === 'inProgress' || rev.status === 'returnedByLineManager') return true;
        // Mid-year only actionable for staff before they've submitted their comments
        if (rev.status === 'midYearOpen') return !(rev.midYearReview?.staffSubmittedAt);
        return false;
      }).length;
    }
    return 0;
  }, [isLM, isStaff, lmReviews, staffReviews, uid]);
}

// ─── Notification bell ───────────────────────────────────────────────────────

const NOTIF_TYPE_ICONS: Record<string, string> = {
  review_submitted: '📋',
  review_returned:  '↩️',
  review_confirmed: '✅',
  mid_year_open:    '📅',
};

function NotificationBell({ uid }: { uid: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: rawNotifs } = useCollection<AppNotification>(
    uid
      ? query(notificationsCol, where('userId', '==', uid), orderBy('createdAt', 'desc'), limit(20))
      : null,
    uid,
  );

  const notifs = rawNotifs as (AppNotification & { id: string })[];
  const unread = notifs.filter((n) => !n.read).length;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = async (notif: AppNotification & { id: string }) => {
    if (notif.read) return;
    await updateDoc(doc(db, 'notifications', notif.id), { read: true });
  };

  const markAllRead = async () => {
    await Promise.all(notifs.filter((n) => !n.read).map((n) => updateDoc(doc(db, 'notifications', n.id), { read: true })));
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
        aria-label="Notifications"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-bold text-white leading-none">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-50 w-80 rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-xs font-medium text-brand-600 hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          {notifs.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-400">No notifications yet.</div>
          ) : (
            <ul className="max-h-96 overflow-y-auto divide-y divide-gray-50">
              {notifs.map((n) => (
                <li key={n.id}>
                  <Link
                    to={`/reviews/${n.reviewId}`}
                    onClick={() => { void markRead(n); setOpen(false); }}
                    className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${n.read ? 'opacity-60' : ''}`}
                  >
                    <span className="text-lg leading-none mt-0.5 shrink-0">{NOTIF_TYPE_ICONS[n.type] ?? '🔔'}</span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm leading-snug ${n.read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>{n.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5 leading-snug">{n.body}</p>
                    </div>
                    {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-brand-500 shrink-0" />}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────

export default function Layout() {
  const { appUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwResetState, setPwResetState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  // Tracks immediate local acceptance so the gate closes without waiting for Firestore snapshot
  const [legalAcceptedLocally, setLegalAcceptedLocally] = useState(false);
  const actionableCount = useActionableCount();

  const visibleItems = NAV_ITEMS.filter(
    (item) => appUser && item.roles.includes(appUser.role),
  );

  const { data: school } = useDocument<School>(
    appUser?.schoolId ? schoolDoc(appUser.schoolId) : null,
  );

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  // Auto sign-out after 5 minutes of inactivity, with a 1-minute warning
  useIdleTimeout(
    () => { setShowIdleWarning(false); void handleSignOut(); },
    5 * 60 * 1000,
    !!appUser,
    () => setShowIdleWarning(true),
    60 * 1000,
    () => setShowIdleWarning(false),
  );

  const handleChangePassword = async () => {
    if (!appUser?.email || pwResetState === 'sending') return;
    setPwResetState('sending');
    try {
      const fns = getFunctions(app, 'europe-west2');
      const resetFn = httpsCallable<{ email: string }, { success: boolean }>(fns, 'requestPasswordReset');
      await resetFn({ email: appUser.email });
      setPwResetState('sent');
    } catch {
      setPwResetState('error');
    }
  };

  return (
    <div className="fixed inset-0 flex overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${mobileOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-40 w-56 bg-brand-900 text-white transition-transform md:relative md:translate-x-0`}
      >
        <div className="flex h-16 items-center px-4 border-b border-brand-700">
          <LogoIcon variant="bars" size={28} theme="light" />
        </div>

        {/* School name — prominent, below logo */}
        {school && (
          <div className="px-4 py-3 border-b border-brand-800">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-400 mb-0.5">School</p>
            <p className="text-sm font-semibold text-white leading-snug">{school.name}</p>
          </div>
        )}

        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/admin' || item.to === '/school'}
              className={({ isActive }) =>
                `flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-700 text-white'
                    : 'text-brand-100 hover:bg-brand-800'
                }`
              }
              onClick={() => setMobileOpen(false)}
            >
              <span>{item.label}</span>
              {item.to === '/todo' && actionableCount > 0 && (
                <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
                  {actionableCount > 99 ? '99+' : actionableCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-brand-700 px-4 py-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-brand-300 truncate flex-1 min-w-0">
              {appUser?.displayName}
            </p>
            {appUser && <NotificationBell uid={appUser.id} />}
          </div>
          <p className="text-xs text-brand-400 mb-2">
            {appUser ? ROLE_LABELS[appUser.role] : ''}
          </p>
          <div className="flex items-center gap-3 mb-2">
            <Link
              to="/faq"
              className="text-xs text-brand-300 hover:text-white transition-colors"
            >
              Help &amp; FAQ
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => void handleChangePassword()}
              disabled={pwResetState === 'sending'}
              className="text-xs text-brand-300 hover:text-white transition-colors disabled:opacity-50"
            >
              {pwResetState === 'sending' ? 'Sending…' : 'Change password'}
            </button>
            <span className="text-brand-600 text-xs">·</span>
            <button
              onClick={() => void handleSignOut()}
              className="text-xs text-brand-300 hover:text-white transition-colors"
            >
              Sign out
            </button>
          </div>
          {pwResetState === 'sent' && (
            <p className="mt-1.5 text-xs text-green-400">
              Reset link sent to your email.
            </p>
          )}
          {pwResetState === 'error' && (
            <p className="mt-1.5 text-xs text-red-400">
              Failed to send. Please try again.
            </p>
          )}
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Idle warning modal */}
      {showIdleWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4 text-center">
            <div className="text-4xl">⏱</div>
            <h2 className="text-lg font-semibold text-gray-900">Still there?</h2>
            <p className="text-sm text-gray-500">
              You'll be signed out in <strong>1 minute</strong> due to inactivity.
            </p>
            <button
              type="button"
              onClick={() => setShowIdleWarning(false)}
              className="btn-primary w-full"
            >
              Stay signed in
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        {/* Top bar (mobile) */}
        <header className="flex h-16 items-center gap-3 border-b border-gray-200 bg-white px-4 md:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            className="text-gray-500 hover:text-gray-700 shrink-0"
            aria-label="Open menu"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <LogoIcon variant="bars" size={24} theme="dark" />
          {school && (
            <>
              <span className="text-gray-200 text-sm">|</span>
              <span className="text-sm font-semibold text-gray-700 truncate">{school.name}</span>
            </>
          )}
          <div className="ml-auto shrink-0">
            {appUser && <NotificationBell uid={appUser.id} />}
          </div>
        </header>

        {/* Legal gate — shown to school admins until they accept the current version of ToS + DPA */}
        {appUser?.role === 'schoolAdmin' && school && !legalAcceptedLocally && (!school.legalAcceptedAt || school.legalVersion !== '1.0') ? (
          <LegalGate
            schoolId={appUser.schoolId ?? ''}
            schoolAdminId={appUser.id}
            schoolAdminName={appUser.displayName}
            onAccepted={() => setLegalAcceptedLocally(true)}
          />
        ) : (
          <main className="flex-1 overflow-y-auto p-4 sm:p-6">
            <Outlet />
          </main>
        )}
      </div>
    </div>
  );
}
